import {
  Autocomplete,
  Button,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  tableCellClasses,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { AiFillCloseCircle, AiOutlineMinus } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { MdModeEdit, MdOutlineAdd, MdOutlineUpdate } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getBankDropdownData,
  getMasterLoanTypes,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import { StyledIconButton, StyledTableRow } from '../../utils/StyleTable';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import { getOnboardingShowData } from '../../store/Slice/Onboarding/OnboardingSlice';
import { OnboardingApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';

const validationSchema = yup.object().shape({
  partnerCommission: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          bankId: yup.string().required('Please select bank name'),
          loanTypeId: yup.string().required('Please select loan type'),
          companyCommition: yup
            .number()
            .typeError('Type company commission %')
            .required('Type company commission %')
            .min(0, 'Commission cannot be negative')
            .max(100, 'Commission cannot exceed 100%'),
          partnerCommition: yup
            .number()
            .typeError('Type partner share %')
            .required('Type partner share %')
            .min(0, 'Partner share cannot be negative')
            .max(100, 'Partner share cannot exceed 100%')
        })
        .test(
          'total-not-greater-than-100',
          'Sum of Company Commission & Partner Share should not be greater than 100%',
          function (item) {
            const total = Number(item.companyCommition || 0) + Number(item.partnerCommition || 0);
            if (total > 100) {
              return this.createError({
                path: `${this.path}.partnerCommition`, // attach error to partnerCommition
                message: 'Sum of Company Commission & Partner Share should not be greater than 100%'
              });
            }
            return true;
          }
        )
    )
    .min(1, 'At least one partner commission entry is required')
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

function SetCommissionOnBoarding() {
  const [editcomm, setEditcomm] = useState('');
  const [autosugval, setAutosugval] = useState({
    loanTypeId: '',
    companyCommition: '',
    partnerCommition: ''
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location?.state || {};
  // console.log('rowIdrowIdrowId', rowId);
  const { LoginData } = useLoginSelector();
  const { OnboardingShowCommision } = useOnboardingSelector();
  const { BankDrop, MasterLoanTypeDrop } = useGlobalSelector();

  const newBankDrop = [{ label: 'All', value: 0 }, ...BankDrop] || [];
  const newMasterLoanTypeDrop = [{ label: 'All', value: 0 }, ...MasterLoanTypeDrop] || [];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      partnerCommission: [
        {
          bankId: '',
          loanTypeId: '',
          companyCommition: '',
          partnerCommition: ''
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partnerCommission'
  });

  const handleAddRow = () => {
    append({
      bankId: '',
      loanTypeId: '',
      companyCommition: '',
      partnerCommition: ''
    });
  };

  const handleRemoveRow = (index) => remove(index);

  const firedApiPartnerUpdate = (commitionId, index) => {
    let isCond = true;
    if (
      OnboardingShowCommision?.[index].companyCommition !== '' &&
      OnboardingShowCommision?.[index].partnerCommition !== ''
    ) {
      const total = Number(autosugval.companyCommition) + Number(autosugval.partnerCommition);
      if (total > 100) {
        isCond = false;
        dispatch(
          setApiError('Sum of Company Commission & Partner Share should not be greater then 100%')
        );
        autosugval.companyCommition = 0;
        autosugval.partnerCommition = 0;
        return true;
      }
    }

    if (isCond) {
      (async () => {
        const credential = {
          companyId: LoginData?.companyId,
          partnerId: rowId?.partnerId || rowId,
          partnerCommissions: [
            {
              companyCommition: autosugval?.companyCommition,
              partnerCommition: autosugval?.partnerCommition,
              bankId: autosugval?.bankId,
              loanTypeId: autosugval?.loanTypeId,
              commitionId: commitionId
            }
          ]
        };
        const result = await dispatch(saveApiData(OnboardingApi?.onboardSetCommission, credential));
        if (result?.status === 'success') {
          getOnboardingShowDataApi();
          removecommission();
        }
      })();
    }
  };

  const editcommission = (data) => {
    setEditcomm(data?.commitionId);
    setAutosugval({
      bankId: data?.bankId,
      loanTypeId: data?.loanTypeId,
      companyCommition: data?.companyCommition,
      partnerCommition: data?.partnerCommition
    });
  };

  const removecommission = () => {
    setEditcomm('');
    setAutosugval({});
  };

  const onSubmit = useCallback(
    async (data) => {
      const credential = {
        companyId: LoginData?.companyId,
        partnerId: rowId?.partnerId || rowId,
        partnerCommissions: data?.partnerCommission
      };
      const result = await dispatch(saveApiData(OnboardingApi?.onboardSetCommission, credential));
      if (result?.status === 'success') {
        getOnboardingShowDataApi();
        reset();
      }
    },
    [LoginData, rowId]
  );

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  const getOnboardingShowDataApi = useCallback(() => {
    if (!LoginData && !rowId) return;
    const data = {
      companyId: LoginData?.companyId,
      partnerId: rowId?.partnerId || rowId,
      sortOrder: 'asc'
    };
    dispatch(getOnboardingShowData(data));
  }, [rowId, LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  useEffect(() => {
    getOnboardingShowDataApi();
  }, [getOnboardingShowDataApi]);

  useEffect(() => {
    dispatch(getMasterLoanTypes());
  }, []);

  return (
    <Page title="Kpros by Kalolytic Solutions || On Boarding">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Set Commission" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Loan Requirement
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <form id="setSubmitForm" onSubmit={handleSubmit(onSubmit)}>
              {fields.map((item, index) => (
                <Grid container alignItems="end" key={item.id} mb={2}>
                  <Grid item xs>
                    <Grid container spacing={3}>
                      {/* Bank */}
                      <Grid item xs={3}>
                        <CustomSelect
                          name={`partnerCommission.${index}.bankId`}
                          label="Bank"
                          control={control}
                          errors={errors?.partnerCommission?.[index]}
                          options={newBankDrop}
                          placeholder="Select Bank Name"
                          isRequired
                          isMultipleError
                        />
                      </Grid>

                      {/* Loan Type */}
                      <Grid item xs={3}>
                        <CustomSelect
                          name={`partnerCommission.${index}.loanTypeId`}
                          label="Select Loan Type"
                          control={control}
                          errors={errors?.partnerCommission?.[index]}
                          options={newMasterLoanTypeDrop}
                          placeholder="Select Loan Type"
                          isRequired
                          isMultipleError
                        />
                      </Grid>

                      {/* Company Commission */}
                      <Grid item xs={3}>
                        <CustomInput
                          name={`partnerCommission.${index}.companyCommition`}
                          label="Company Commission"
                          type="number"
                          control={control}
                          errors={errors?.partnerCommission?.[index]}
                          placeholder="Type Company Commission %"
                          isRequired
                          isMultipleError
                        />
                      </Grid>

                      {/* Partner Share */}
                      <Grid item xs={3}>
                        <CustomInput
                          name={`partnerCommission.${index}.partnerCommition`}
                          label="Partner Share"
                          type="number"
                          control={control}
                          errors={errors?.partnerCommission?.[index]}
                          placeholder="Type Partner Share %"
                          isRequired
                          isMultipleError
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Add Button */}
                  <Grid item xs="auto">
                    <Tooltip
                      title="Add"
                      placement="top"
                      arrow
                      sx={{ marginLeft: '1rem', marginBottom: '4px' }}
                    >
                      <IconButton className="squareIconButton" onClick={handleAddRow}>
                        <MdOutlineAdd size={18} />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  {/* Remove Button */}
                  {fields.length > 1 && (
                    <Grid item xs="auto">
                      <Tooltip
                        title="Remove"
                        placement="top"
                        arrow
                        sx={{ marginLeft: '1rem', marginBottom: '4px' }}
                      >
                        <IconButton
                          className="squareIconButton"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <AiOutlineMinus size={18} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              ))}
            </form>
          </Box>
          <Box className="" mb={3} textAlign="right">
            <Button variant="contained" type="submit" form="setSubmitForm" className="mainBtn">
              Submit & Close
            </Button>
          </Box>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Commission Log
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin viewCardPadding">
            <TableContainer sx={{ minWidth: 800, overflow: 'auto', background: 'white' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '220px' }}>Bank</StyledTableCell>
                    <StyledTableCell style={{ width: '230px' }} align="">
                      Loan Type
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '230px' }} align="">
                      Our Commission
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '230px' }} align="">
                      Partner Share
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '180px' }} align="">
                      Modified on
                    </StyledTableCell>
                    <StyledTableCell style={{ width: '120px' }} align="">
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {OnboardingShowCommision?.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell align="">
                        {editcomm !== item.commitionId ? (
                          newBankDrop?.filter((bankObj) => bankObj.value === item.bankId).length >
                            0 &&
                          newBankDrop?.filter((bankObj) => bankObj.value === item.bankId)[0].label
                        ) : (
                          <Box className="">
                            <Autocomplete
                              value={
                                autosugval.bankId
                                  ? newBankDrop.find((opt) => opt.value === autosugval.bankId) ||
                                    null
                                  : null
                              }
                              id="combo-box-demo3"
                              options={newBankDrop || []}
                              getOptionLabel={(option) => option.label || ''}
                              size="small"
                              renderInput={(params) => (
                                <TextField {...params} fullWidth placeholder="Select Bank Name" />
                              )}
                              renderOption={(props, option) => <li {...props}>{option.label}</li>}
                              onChange={(e, newValue) => {
                                setAutosugval({
                                  ...autosugval,
                                  bankId: newValue ? newValue.value : '', // store value
                                  bankLabel: newValue ? newValue.label : '' // store label
                                });
                              }}
                            />
                          </Box>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="">
                        {editcomm !== item.commitionId ? (
                          newMasterLoanTypeDrop?.filter(
                            (bankObj) => bankObj.value === item.loanTypeId
                          ).length > 0 &&
                          newMasterLoanTypeDrop?.filter(
                            (bankObj) => bankObj.value === item.loanTypeId
                          )[0].label
                        ) : (
                          <Box className="">
                            <Select
                              fullWidth
                              inputProps={{
                                className: `textInput customSelectBox dissabledMenu${autosugval.loanTypeId}`
                              }}
                              value={autosugval.loanTypeId}
                              onChange={(e) => {
                                setAutosugval({ ...autosugval, loanTypeId: e.target.value });
                              }}
                            >
                              <MenuItem value=" " disabled>
                                Select Loan Type
                              </MenuItem>
                              {newMasterLoanTypeDrop?.map((item, index) => (
                                <MenuItem value={item.value} key={item.value}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="">
                        {editcomm !== item.commitionId ? (
                          `${item.companyCommition}`
                        ) : (
                          <Box className="">
                            <TextField
                              fullWidth
                              type="number"
                              inputProps={{
                                className: 'textInput'
                              }}
                              value={autosugval.companyCommition}
                              placeholder="Type Company Commission %"
                              onChange={(e) => {
                                setAutosugval({
                                  ...autosugval,
                                  companyCommition: e.target.value
                                });
                              }}
                            />
                          </Box>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="">
                        {editcomm !== item.commitionId ? (
                          `${item.partnerCommition}`
                        ) : (
                          <Box className="">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                  className: 'textInput'
                                }}
                                placeholder="Type Partner Share %"
                                value={autosugval.partnerCommition}
                                onChange={(e) => {
                                  setAutosugval({
                                    ...autosugval,
                                    partnerCommition: e.target.value
                                  });
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="">NA</StyledTableCell>
                      {editcomm !== item.commitionId ? (
                        <StyledTableCell>
                          <Tooltip title="Edit" placement="top" arrow>
                            <StyledIconButton
                              onClick={() => editcommission(item)}
                              className="squareIconButton actionButton"
                            >
                              <MdModeEdit size={14} />
                            </StyledIconButton>
                          </Tooltip>
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell>
                          <Tooltip title="Remove" placement="top" arrow>
                            <StyledIconButton
                              onClick={() => removecommission()}
                              className="squareIconButton actionButton"
                            >
                              <AiFillCloseCircle size={14} />
                            </StyledIconButton>
                          </Tooltip>
                          <Tooltip title="Update" placement="top" arrow>
                            <StyledIconButton
                              className="squareIconButton actionButton"
                              onClick={(e) => firedApiPartnerUpdate(item.commitionId, index)}
                            >
                              <MdOutlineUpdate size={14} />
                            </StyledIconButton>
                          </Tooltip>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
export default SetCommissionOnBoarding;
